import { FieldConfig, ValidationRule } from "../types/formTypes";

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string;
}

export interface FormValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
  atLeastOneError?: string; 
  requiredIfErrors?: Record<string, string>; 
  dateRangeError?: { field: string; message: string }; 
}

export const validateField = (
  value: any,
  validationRules: ValidationRule[],
  formData?: Record<string, any>
): ValidationResult => {
  for (const rule of validationRules) {
    const result = validateRule(value, rule, formData);
    if (!result.isValid) {
      return result;
    }
  }

  return { isValid: true, errorMessage: "" };
};

const validateRule = (
  value: any,
  rule: ValidationRule,
  formData?: Record<string, any>
): ValidationResult => {
  const strValue = value !== undefined && value !== null ? String(value).trim() : "";

  switch (rule.type) {
    case "required":
      return validateRequired(strValue, rule);
    case "requiredIf":
      return validateRequiredIf(strValue, rule, formData);
    case "atLeastOne":
      return validateAtLeastOne(rule, formData);
    case "email":
      return validateEmail(strValue, rule);
    case "phone":
      return validatePhone(strValue, rule);
    case "minLength":
      return validateMinLength(strValue, rule);
    case "maxLength":
      return validateMaxLength(strValue, rule);
    case "min":
      return validateMin(strValue, rule);
    case "max":
      return validateMax(strValue, rule);
    case "minDate":
      return validateMinDate(strValue, rule);
    case "maxDate":
      return validateMaxDate(strValue, rule);
    case "dateBefore":
      return validateDateBefore(strValue, rule, formData);
    case "dateFormat":
      return validateDateFormat(strValue, rule);
    case "pattern":
      return validatePattern(strValue, rule);
    case "custom":
      return validateCustom(value, rule, formData);
  }

  return { isValid: true, errorMessage: "" };
};

const validateRequired = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (!strValue) {
    return {
      isValid: false,
      errorMessage: rule.message || "User Input required.",
    };
  }
  return { isValid: true, errorMessage: "" };
};

const validateRequiredIf = (strValue: string, rule: ValidationRule, formData?: Record<string, any>): ValidationResult => {
  if (rule.condition && formData && rule.condition(formData)) {
    if (!strValue) {
      return {
        isValid: false,
        errorMessage: rule.message || "User Input required.",
      };
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateAtLeastOne = (rule: ValidationRule, formData?: Record<string, any>): ValidationResult => {
  if (rule.relatedFields && formData) {
    const hasValue = rule.relatedFields.some((field) => {
      const fieldValue = formData[field];
      return fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== "";
    });
    if (!hasValue) {
      return {
        isValid: false,
        errorMessage: rule.message || "At least one field must be provided.",
      };
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateEmail = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue) {
    const hasConsecutiveDots = /\.\./.test(strValue);
    const hasInvalidChars = /[^a-zA-Z0-9@._-]/.test(strValue);
    const startsWithDot = strValue.startsWith('.');
    const endsWithDotBeforeAt = /\.@/.test(strValue);
    const hasAtSymbol = /@/.test(strValue);
    const multipleAtSymbols = (strValue.match(/@/g) || []).length > 1;
    const emailRegex = /^[a-zA-Z0-9][a-zA-Z0-9._-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
    
    if (!hasAtSymbol || multipleAtSymbols || hasConsecutiveDots || hasInvalidChars || 
        startsWithDot || endsWithDotBeforeAt || !emailRegex.test(strValue)) {
      return {
        isValid: false,
        errorMessage: rule.message || "Invalid email address. Please enter a valid email.",
      };
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validatePhone = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue) {
    if (strValue.includes('-')) {
      const phoneFormatRegex = /^\d{3}-\d{3}-\d{4}$/;
      if (!phoneFormatRegex.test(strValue)) {
        return {
          isValid: false,
          errorMessage: "Invalid phone number format. Please use XXX-XXX-XXXX format.",
        };
      }
    } else {
      const digitsOnly = strValue.replaceAll(/\D/g, "");
      if (digitsOnly.length !== 10 || strValue !== digitsOnly) {
        return {
          isValid: false,
          errorMessage: rule.message || "Invalid phone number. Please enter a 10-digit phone number.",
        };
      }
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateMinLength = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue && rule.value !== undefined) {
    if (strValue.length < rule.value) {
      return {
        isValid: false,
        errorMessage: rule.message || `Must be at least ${rule.value} characters.`,
      };
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateMaxLength = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue && rule.value !== undefined) {
    if (strValue.length > rule.value) {
      return {
        isValid: false,
        errorMessage: rule.message || `Must not exceed ${rule.value} characters.`,
      };
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateMin = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue && rule.value !== undefined) {
    const numValue = Number(strValue);
    if (!Number.isNaN(numValue) && numValue < rule.value) {
      return {
        isValid: false,
        errorMessage: rule.message || `Enter a valid number.`,
      };
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateMax = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue && rule.value !== undefined) {
    const numValue = Number(strValue);
    if (!Number.isNaN(numValue) && numValue > rule.value) {
      return {
        isValid: false,
        errorMessage: rule.message || `Value must not exceed ${rule.value}.`,
      };
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateMinDate = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue && rule.value) {
    const parts = strValue.split("/").map((p) => Number.parseInt(p, 10));
    if (parts.length === 3 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1]) && !Number.isNaN(parts[2])) {
      const selectedDate = new Date(parts[2], parts[0] - 1, parts[1]);
      selectedDate.setHours(0, 0, 0, 0);
      const [minYear, minMonth, minDay] = String(rule.value).split("-").map((p) => Number.parseInt(p, 10));
      const minDate = new Date(minYear, minMonth - 1, minDay);
      minDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < minDate) {
        return {
          isValid: false,
          errorMessage: rule.message || `Date must be on or after ${formatDate(minDate)}.`,
        };
      }
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateMaxDate = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue && rule.value) {
    const parts = strValue.split("/").map((p) => Number.parseInt(p, 10));
    if (parts.length === 3 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1]) && !Number.isNaN(parts[2])) {
      const selectedDate = new Date(parts[2], parts[0] - 1, parts[1]);
      selectedDate.setHours(0, 0, 0, 0);
      const [maxYear, maxMonth, maxDay] = String(rule.value).split("-").map((p) => Number.parseInt(p, 10));
      const maxDate = new Date(maxYear, maxMonth - 1, maxDay);
      maxDate.setHours(0, 0, 0, 0);     
      if (selectedDate > maxDate) {
        return {
          isValid: false,
          errorMessage: rule.message || `Date must be on or before ${formatDate(maxDate)}.`,
        };
      }
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateDateBefore = (strValue: string, rule: ValidationRule, formData?: Record<string, any>): ValidationResult => {
  if (strValue && rule.compareField && formData) {
    const compareValue = formData[rule.compareField];
    if (compareValue) {
      const parts1 = strValue.split("/").map((p) => Number.parseInt(p, 10));
      const parts2 = String(compareValue).split("/").map((p) => Number.parseInt(p, 10));
      
      if (
        parts1.length === 3 && !Number.isNaN(parts1[0]) && !Number.isNaN(parts1[1]) && !Number.isNaN(parts1[2]) &&
        parts2.length === 3 && !Number.isNaN(parts2[0]) && !Number.isNaN(parts2[1]) && !Number.isNaN(parts2[2])
      ) {
        const date1 = new Date(parts1[2], parts1[0] - 1, parts1[1]);
        date1.setHours(0, 0, 0, 0);
        const date2 = new Date(parts2[2], parts2[0] - 1, parts2[1]);
        date2.setHours(0, 0, 0, 0);
        if (date1 < date2) {
          return {
            isValid: false,
            errorMessage: rule.message || `Date must be later than ${formatDate(date2)}.`,
          };
        }
      }
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateDateFormat = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue) {
    const dateRegex = /^(0?\d|1[0-2])\/(0?\d|[12]\d|3[01])\/\d{4}$/;
    if (!dateRegex.test(strValue)) {
      return {
        isValid: false,
        errorMessage: rule.message || "All date fields must follow the correct format (MM/DD/YYYY).",
      };
    }
    const parts = strValue.split("/").map((p) => Number.parseInt(p, 10));
    if (parts.length === 3) {
      const month = parts[0];
      const day = parts[1];
      const year = parts[2];
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        return {
          isValid: false,
          errorMessage: rule.message || "All date fields must follow the correct format (MM/DD/YYYY).",
        };
      }
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validatePattern = (strValue: string, rule: ValidationRule): ValidationResult => {
  if (strValue && rule.value) {
    const regex = new RegExp(rule.value);
    if (!regex.test(strValue)) {
      return {
        isValid: false,
        errorMessage: rule.message || "Invalid format.",
      };
    }
  }
  return { isValid: true, errorMessage: "" };
};

const validateCustom = (value: any, rule: ValidationRule, formData?: Record<string, any>): ValidationResult => {
  if (rule.validator) {
    const isValid = rule.validator(value, formData);
    if (!isValid) {
      return {
        isValid: false,
        errorMessage: rule.message || "Validation failed.",
      };
    }
  }
  return { isValid: true, errorMessage: "" };
};

// Helper functions for form validation
const validateAtLeastOneFields = (
  fields: FieldConfig[],
  formData: Record<string, any>
): { error: string | undefined; isValid: boolean } => {
  for (const field of fields) {
    if (field.validations && field.validations.length > 0) {
      const atLeastOneRule = field.validations.find(rule => rule.type === 'atLeastOne');
      if (atLeastOneRule) {
        const result = validateAtLeastOne(atLeastOneRule, formData);
        if (!result.isValid) {
          return { error: result.errorMessage, isValid: false };
        }
      }
    }
  }
  return { error: undefined, isValid: true };
};

const validateSpecificRequiredIfField = (
  fields: FieldConfig[],
  formData: Record<string, any>,
  fieldName: string
): { error: Record<string, string>; isValid: boolean } => {
  const field = fields.find(f => f.name === fieldName);
  if (field?.validations) {
    const value = formData[field.name];
    const requiredIfRule = field.validations.find(rule => rule.type === 'requiredIf');
    
    if (requiredIfRule) {
      const strValue = value !== undefined && value !== null ? String(value).trim() : "";
      const result = validateRequiredIf(strValue, requiredIfRule, formData);
      
      if (!result.isValid) {
        return { error: { [field.name]: result.errorMessage }, isValid: false };
      }
    }
  }
  return { error: {}, isValid: true };
};

const processRequiredIfValidation = (
  field: FieldConfig,
  formData: Record<string, any>
): { fieldName: string; error: string } | null => {
  const value = formData[field.name];
  const requiredIfRule = field.validations?.find(rule => rule.type === 'requiredIf');
  
  if (!requiredIfRule) return null;
  
  const strValue = value !== undefined && value !== null ? String(value).trim() : "";
  const result = validateRequiredIf(strValue, requiredIfRule, formData);
  
  if (!result.isValid) {
    return { fieldName: field.name, error: result.errorMessage };
  }
  
  return null;
};

const validateAllRequiredIfFields = (
  fields: FieldConfig[],
  formData: Record<string, any>,
  excludeFields: string[]
): { errors: Record<string, string>; isValid: boolean } => {
  const requiredIfErrors: Record<string, string> = {};

  for (const field of fields) {
    // Skip excluded fields
    if (excludeFields.includes(field.name)) continue;
    if (!field.validations || field.validations.length === 0) continue;
    
    const validationResult = processRequiredIfValidation(field, formData);
    if (validationResult) {
      requiredIfErrors[validationResult.fieldName] = validationResult.error;
    }
  }

  return { 
    errors: requiredIfErrors, 
    isValid: Object.keys(requiredIfErrors).length === 0 
  };
};

const processStandardFieldValidation = (
  field: FieldConfig,
  formData: Record<string, any>
): { fieldName: string; error: string } | null => {
  if (!field.validations || field.validations.length === 0) return null;
  
  const value = formData[field.name];
  const otherValidations = field.validations.filter(
    rule => rule.type !== 'atLeastOne' && rule.type !== 'requiredIf' && rule.type !== 'dateBefore'
  );
  
  if (otherValidations.length === 0) return null;
  
  const result = validateField(value, otherValidations, formData);
  
  if (!result.isValid) {
    return { fieldName: field.name, error: result.errorMessage };
  }
  
  return null;
};

const validateStandardFields = (
  fields: FieldConfig[],
  formData: Record<string, any>
): { errors: Record<string, string>; isValid: boolean } => {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    const validationResult = processStandardFieldValidation(field, formData);
    if (validationResult) {
      errors[validationResult.fieldName] = validationResult.error;
    }
  }

  return { 
    errors, 
    isValid: Object.keys(errors).length === 0 
  };
};

const processDateBeforeValidation = (
  field: FieldConfig,
  formData: Record<string, any>
): { field: string; message: string } | null => {
  if (!field.validations || field.validations.length === 0) return null;
  
  const value = formData[field.name];
  const dateBeforeRule = field.validations.find(rule => rule.type === 'dateBefore');
  
  if (!dateBeforeRule) return null;
  
  const strValue = value !== undefined && value !== null ? String(value).trim() : "";
  const result = validateDateBefore(strValue, dateBeforeRule, formData);
  
  if (!result.isValid) {
    return { field: field.name, message: result.errorMessage };
  }
  
  return null;
};

const validateDateBeforeFields = (
  fields: FieldConfig[],
  formData: Record<string, any>
): { error: { field: string; message: string } | undefined; isValid: boolean } => {
  for (const field of fields) {
    const validationResult = processDateBeforeValidation(field, formData);
    if (validationResult) {
      return { error: validationResult, isValid: false };
    }
  }
  return { error: undefined, isValid: true };
};

export const validateForm = (
  fields: FieldConfig[],
  formData: Record<string, any>
): FormValidationResult => {
  // Step 1: Validate "at least one" fields
  const atLeastOneResult = validateAtLeastOneFields(fields, formData);
  if (!atLeastOneResult.isValid) {
    return { 
      isValid: false, 
      errors: {}, 
      atLeastOneError: atLeastOneResult.error 
    };
  }

  // Step 2: Validate specific requiredIf field (n400ApplicationDate)
  const n400Result = validateSpecificRequiredIfField(fields, formData, 'n400ApplicationDate');
  if (!n400Result.isValid) {
    return { 
      isValid: false, 
      errors: {}, 
      requiredIfErrors: n400Result.error 
    };
  }

  // Step 3: Validate specific requiredIf field (totalClassFees)
  const classFeeResult = validateSpecificRequiredIfField(fields, formData, 'totalClassFees');
  if (!classFeeResult.isValid) {
    return { 
      isValid: false, 
      errors: {}, 
      requiredIfErrors: classFeeResult.error 
    };
  }

  // Step 4: Validate all other requiredIf fields
  const requiredIfResult = validateAllRequiredIfFields(
    fields, 
    formData, 
    ['n400ApplicationDate', 'totalClassFees']
  );
  if (!requiredIfResult.isValid) {
    return { 
      isValid: false, 
      errors: {}, 
      requiredIfErrors: requiredIfResult.errors 
    };
  }

  // Step 5: Validate standard fields (required, email, phone, etc.)
  const standardResult = validateStandardFields(fields, formData);
  if (!standardResult.isValid) {
    return { 
      isValid: false, 
      errors: standardResult.errors 
    };
  }

  // Step 6: Validate dateBefore fields
  const dateBeforeResult = validateDateBeforeFields(fields, formData);
  if (!dateBeforeResult.isValid) {
    return { 
      isValid: false, 
      errors: {}, 
      dateRangeError: dateBeforeResult.error 
    };
  }

  // All validations passed
  return { isValid: true, errors: {} };
};

const formatDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export default {
  validateField,
  validateForm,
};
